import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { AutoAwesome as AIIcon, Handyman as ManualIcon, ViewColumn } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ExperimentCard from './ExperimentCard';
import BlazingFastCheckbox from './BlazingFastCheckbox';
import ExperimentBadge from './ExperimentBadge';
import ExperimentCardMenu from './ExperimentCardMenu';
import SelectionAwareDropdown from './SelectionAwareDropdown';
import DeleteConfirmationPopover from './DeleteConfirmationPopover';
import ExtendExperimentPopover from './ExtendExperimentPopover';
import { useSelectionContext } from '../../contexts/SelectionContext';
import { API_BASE_URL } from '../../config/api.config';
import { experimentColors } from '../../themes/experimentColors';
import { colors } from '../../themes/typography';

/**
 * SelectableExperimentCard - A card that participates in selection
 * 
 * This component uses SelectionContext to:
 * - Know if it's selected
 * - Show dropdown when selected
 * - Handle both single and bulk operations through the same dropdown
 */
const SelectableExperimentCard = ({
  // Core props
  item,
  productKey,
  clientId,
  
  // Display props
  showStoreId = true,
  showBadge = true,
  handleStoreIdClick,
  
  // All other ExperimentCard props
  ...experimentCardProps
}) => {
  const { t } = useTranslation();
  
  // Get selection state from context
  const { 
    isSelected, 
    toggleSelection, 
    selectedCount, 
    onSingleStatusChange,
    onBulkStatusChange,
    clearSelection,
    onDataRefresh 
  } = useSelectionContext();
  
  const isThisCardSelected = isSelected(item.id);
  const experimentData = item.experiment || item;

  // Automated slot-change lineage (appended by the nightly reconcile with [AUTO- markers)
  // surfaces on hover; plain operator feedback (e.g. /combo notes) must NOT trigger it.
  // The [AUTO-…] markers are machine bookkeeping — strip them so readers see plain sentences.
  const lineage = experimentData?.feedback && String(experimentData.feedback).includes('[AUTO-')
    ? String(experimentData.feedback)
        .split('\n')
        .map(line => line.replace(/^\[AUTO-[^\]]*\]\s*/, ''))
        .join('\n\n')
    : null;
  
  // State for delete/extend functionality
  const [deleteAnchorEl, setDeleteAnchorEl] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [extendAnchorEl, setExtendAnchorEl] = React.useState(null);
  const [extendLoading, setExtendLoading] = React.useState(false);
  
  // Get experiment ID - handle different data structures
  const experimentId = item.id || experimentData.id;
  
  // Delete handlers
  const handleDeleteClick = (event) => {
    event.stopPropagation();
    setDeleteAnchorEl(event.currentTarget);
  };
  
  const handleDeleteClose = () => {
    setDeleteAnchorEl(null);
  };
  
  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoading(true);
      
      const deleteUrl = `${API_BASE_URL}/experiments/${experimentId}?clientId=${clientId}`;
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        handleDeleteClose();
        // Clear selection if this card was selected
        if (isThisCardSelected) {
          clearSelection();
        }
        // Trigger data refresh
        if (onDataRefresh) {
          await onDataRefresh();
        }
      } else {
        const error = await response.json();
        console.error('Failed to delete experiment:', error);
        alert(`Failed to delete experiment: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to delete experiment:', error);
      alert(`Failed to delete experiment: ${error.message || 'Network error'}`);
    } finally {
      setDeleteLoading(false);
    }
  };
  
  // Extend handlers
  const handleExtendClick = (event) => {
    event.stopPropagation();
    setExtendAnchorEl(event.currentTarget);
  };
  
  const handleExtendClose = () => {
    setExtendAnchorEl(null);
  };
  
  const handleExtendConfirm = async (data) => {
    try {
      setExtendLoading(true);
      
      const extendUrl = `${API_BASE_URL}/experiments/${experimentId}/extend`;
      
      const response = await fetch(extendUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId: clientId,
          dateFrom: data.dateFrom,
          dateTo: data.dateTo,
          status: data.status
        })
      });
      
      if (response.ok) {
        handleExtendClose();
        // Trigger data refresh
        if (onDataRefresh) {
          await onDataRefresh();
        }
      } else {
        const error = await response.json();
        console.error('Failed to extend experiment:', error);
        alert(`Failed to extend experiment: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to extend experiment:', error);
      alert(`Failed to extend experiment: ${error.message || 'Network error'}`);
    } finally {
      setExtendLoading(false);
    }
  };
  
  // We need to manually build the controls to insert dropdown in the middle
  const headerControls = (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      {/* Left side - checkbox, store ID, badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
        <BlazingFastCheckbox
          experimentId={item.id}
          isSelected={isThisCardSelected}
          onToggleSelection={() => toggleSelection(item.id)}
        />
        
        {/* Machine ID Pill */}
        {showStoreId && item.store_id && (
          <Tooltip title={`${item.group || ''} - ${item.location || ''}`} arrow>
            <Box
              onClick={handleStoreIdClick ? () => handleStoreIdClick(item) : undefined}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: '20px',
                px: '8px',
                backgroundColor: 'rgba(107, 114, 128, 0.08)',
                borderRadius: '10px',
                border: '1px solid rgba(107, 114, 128, 0.12)',
                transition: 'all 0.2s',
                cursor: handleStoreIdClick ? 'pointer' : 'default',
                '&:hover': handleStoreIdClick ? {
                  backgroundColor: 'rgba(107, 114, 128, 0.12)',
                  borderColor: 'rgba(107, 114, 128, 0.2)',
                } : {}
              }}
            >
              <Typography sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#6b7280',
                lineHeight: 1,
              }}>
                {item.store_id}
              </Typography>
            </Box>
          </Tooltip>
        )}
        
        {/* Badge */}
        {showBadge && experimentData?.experimentType && (
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            height: '20px',
            px: '6px',
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
            borderRadius: '10px',
            border: '1px solid rgba(59, 130, 246, 0.1)',
          }}>
            <ExperimentBadge 
              experimentType={experimentData.experimentType} 
              t={t}
            />
          </Box>
        )}
        
        {/* Slot indicator — lineage rides on this chip because it renders in summarized mode
            (the management page) where the status pill does not */}
        {experimentData?.slots?.length > 0 && (
          <Tooltip
            title={lineage
              ? (
                <span style={{ whiteSpace: 'pre-line' }}>
                  {`${t('slotsMultiple', { slots: experimentData.slots.map(s => s.selectionId).join(', ') })}\n\n${lineage}`}
                </span>
              )
              : t('slotsMultiple', { slots: experimentData.slots.map(s => s.selectionId).join(', ') })}
            arrow
            enterDelay={200}
          >
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              height: '20px',
              px: '5px',
              backgroundColor: 'rgba(59, 130, 246, 0.06)',
              borderRadius: '10px',
              border: '1px solid rgba(59, 130, 246, 0.12)',
              cursor: 'default',
            }}>
              <ViewColumn sx={{ fontSize: 11, color: colors.primary.main, opacity: 0.7 }} />
              <Typography sx={{
                fontSize: '10px',
                fontWeight: 600,
                color: colors.primary.main,
                lineHeight: 1,
                opacity: 0.8,
              }}>
                {experimentData.slots.length}
              </Typography>
            </Box>
          </Tooltip>
        )}

        {/* Status Pill - moved from ExperimentCard */}
        {!experimentCardProps.summarized && (
          <>
            {(() => {
              const withLineageTooltip = (pill) => lineage ? (
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{lineage}</span>} arrow placement="top">
                  {pill}
                </Tooltip>
              ) : pill;
              if (experimentData?.status) {
                switch (experimentData.status) {
                  case "pending":
                    return withLineageTooltip(
                      <Box sx={{
                        backgroundColor: `${experimentColors.pending}15`,
                        color: experimentColors.pending,
                        px: '6px',
                        py: '2px',
                        borderRadius: '12px',
                        fontSize: '8px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        border: `1px solid ${experimentColors.pending}`,
                        display: 'inline-block'
                      }}>
                        {t('pending')}
                      </Box>
                    );
                  case "accepted":
                    return withLineageTooltip(
                      <Box sx={{
                        backgroundColor: `${experimentColors.accepted}15`,
                        color: experimentColors.accepted,
                        px: '6px',
                        py: '2px',
                        borderRadius: '12px',
                        fontSize: '8px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        border: `1px solid ${experimentColors.accepted}`,
                        display: 'inline-block'
                      }}>
                        {t('accepted')}
                      </Box>
                    );
                  case "running":
                    return withLineageTooltip(
                      <Box sx={{
                        backgroundColor: `${experimentColors.running}15`,
                        color: experimentColors.running,
                        px: '6px',
                        py: '2px',
                        borderRadius: '12px',
                        fontSize: '8px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        border: `1px solid ${experimentColors.running}`,
                        display: 'inline-block'
                      }}>
                        {t('running')}
                      </Box>
                    );
                  case "rejected":
                    return withLineageTooltip(
                      <Box sx={{
                        backgroundColor: `${experimentColors.rejected}15`,
                        color: experimentColors.rejected,
                        px: '6px',
                        py: '2px',
                        borderRadius: '12px',
                        fontSize: '8px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        border: `1px solid ${experimentColors.rejected}`,
                        display: 'inline-block'
                      }}>
                        {t('rejected')}
                      </Box>
                    );
                  case "finished":
                    return withLineageTooltip(
                      <Box sx={{
                        backgroundColor: `${experimentColors.finished}15`,
                        color: experimentColors.finished,
                        px: '6px',
                        py: '2px',
                        borderRadius: '12px',
                        fontSize: '8px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        border: `1px solid ${experimentColors.finished}`,
                        display: 'inline-block'
                      }}>
                        {t('finished')}
                      </Box>
                    );
                  default:
                    return null;
                }
              }
              if (item.problemType === 'Critical' || item.problem === 'Critical') {
                return (
                  <Box sx={{
                    backgroundColor: colors.critical.background,
                    color: colors.critical.text,
                    px: '6px',
                    py: '2px',
                    borderRadius: '12px',
                    fontSize: '8px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    border: `1px solid ${colors.critical.text}`,
                    display: 'inline-block'
                  }}>
                    {t('critical')}
                  </Box>
                );
              }
              return null;
            })()}
          </>
        )}
        
        {/* Show dropdown when this card is selected - after badges */}
        {isThisCardSelected && experimentData.status && (
          <SelectionAwareDropdown
            itemId={item.id}
            currentStatus={experimentData.status}
            onSingleStatusChange={onSingleStatusChange}
            onBulkStatusChange={onBulkStatusChange}
            disabled={false}
            loading={false}
            size="compact"
          />
        )}
      </Box>
      
      {/* Right side - menu */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* AI/Manual indicator to the left of menu */}
        {(() => {
          const isAI = item?.is_ai ?? item?.isAI ?? experimentData?.isAI ?? false;
          const tooltip = isAI ? (t('createdByAI') || 'Created by Myma AI') : (t('createdManually') || 'Created manually');
          return (
            <Tooltip title={tooltip} arrow>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isAI ? (
                  <Box
                    sx={{
                      width: 27,
                      height: 27,
                      borderRadius: '50%',
                      backgroundColor: colors.accent.purple + '10',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <AIIcon sx={{ fontSize: 20, color: '#a78bfa' }} />
                  </Box>
                ) : (
                  <ManualIcon sx={{ fontSize: 16, color: '#64748b' }} />
                )}
              </Box>
            </Tooltip>
          );
        })()}

        <ExperimentCardMenu 
        menuItems={[
          {
            key: 'extend',
            label: t('extendExperiment'),
            onClick: handleExtendClick,
          },
          // Only show promotion menu for pending, accepted, and running experiments
          ...(experimentData?.status && ['pending', 'accepted', 'running'].includes(experimentData.status) ? [{
            key: 'promotionPreview',
            label: ['reemplazo_producto', 'ampliar_carril'].includes(experimentData?.experimentType) ? (
              <Box>
                <Typography sx={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.5)' }}>
                  {experimentData.status === 'running' ? t('promotionView') : t('promotionPreview')}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: '#6b7280', mt: '2px' }}>
                  {t('replacementNotInQr')}
                </Typography>
              </Box>
            ) : (experimentData.status === 'running' ? t('promotionView') : t('promotionPreview')),
            onClick: () => {
              if (item.promotionUrl && !['reemplazo_producto', 'ampliar_carril'].includes(experimentData?.experimentType)) {
                window.open(item.promotionUrl, '_blank');
              }
            },
            disabled: ['reemplazo_producto', 'ampliar_carril'].includes(experimentData?.experimentType),
            sx: ['reemplazo_producto', 'ampliar_carril'].includes(experimentData?.experimentType) ? {
              cursor: 'not-allowed',
              '&:hover': {
                backgroundColor: 'transparent',
              }
            } : {},
          }] : []),
          {
            divider: true
          },
          {
            key: 'delete',
            label: t('deleteExperiment'),
            onClick: handleDeleteClick,
            sx: {
              color: '#ef4444',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.04)',
              }
            }
          }
        ]}
        />
      </Box>
    </Box>
  );
  
  // Render the card with header controls
  return (
    <>
      <ExperimentCard
        {...experimentCardProps}
        item={item}
        productKey={productKey}
        clientId={clientId}
        isSelected={isThisCardSelected}
        headerControls={headerControls}
      />
      
      {/* Delete confirmation popover */}
      <DeleteConfirmationPopover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        message={t('deleteExperimentConfirm') || "Delete this experiment?"}
      />
      
      {/* Extend experiment popover */}
      <ExtendExperimentPopover
        open={Boolean(extendAnchorEl)}
        anchorEl={extendAnchorEl}
        onClose={handleExtendClose}
        onExtend={handleExtendConfirm}
        currentEndDate={experimentData?.dateTo}
        loading={extendLoading}
        t={t}
      />
    </>
  );
};

export default SelectableExperimentCard;
